import React, { ComponentClass, ComponentType, PureComponent, ReactNode } from 'react';
import { Translate } from 'i18n';
import { isAbortError, isNotAllowedError } from 'common/modules/media/isAbortError';
import { SErrorRetryButton, SErrorTitle, SErrorWrapper } from '../styled';

interface IState {
  hasError: boolean;
}

export const withVideoErrorBoundary = <P extends Record<string, any>>(
  // An indexable props bag, not bare `ComponentType` (which is `ComponentType<{}>`):
  // the boundary injects onReady/onError, and `{}` props reject any forwarded prop.
  WrappedComponent: ComponentType<Record<string, unknown>>
): ComponentClass<P> =>
  class extends PureComponent<P, IState> {
    /**
     * Whether the player reported readiness for the *current* url. Gates the media-error
     * branch below: react-player routes `play()` rejections into `onError`
     * (`FilePlayer.js:263`), so treating every `onError` as fatal would replace a working
     * player with an error screen the moment autoplay hit the gesture policy. An instance
     * field, not state — it must not trigger a render.
     */
    private hasReportedReady = false;

    constructor(props: P) {
      super(props);
      this.state = { hasError: false };
    }

    // Needed as well as componentDidCatch, which only runs after React has already
    // re-rendered the throwing child.
    static getDerivedStateFromError(): IState {
      return { hasError: true };
    }

    componentDidUpdate(prevProps: P): void {
      if (prevProps.url === this.props.url) return;

      // Consumers such as MinimalPairs advance items by mutating `url` on one mounted
      // boundary: without this reset, one failure on item 2 showed the error for 3..n too.
      this.hasReportedReady = false;

      if (this.state.hasError) {
        this.setState({ hasError: false });
      }
    }

    componentDidCatch(): void {
      this.setState({ hasError: true });
      this.props.onVideoPlayerError && this.props.onVideoPlayerError();
    }

    private handleReady = (...args: unknown[]): void => {
      this.hasReportedReady = true;
      this.props.onReady && this.props.onReady(...args);
    };

    // Media errors (a failed/blocked source) never reach componentDidCatch — they arrive
    // as an `onError` callback from the player.
    private handleError = (...args: unknown[]): void => {
      const { onError, onVideoPlayerError } = this.props;

      // A source that never became ready has failed to load — the blocked-embed case (a
      // blocked youtube-nocookie.com rejects in getSDK and lands here), which otherwise
      // leaves a visible empty box. AbortError/NotAllowedError are excluded: react-player
      // routes every play() rejection into onError, and a src change rejects the pending
      // play() with AbortError — arriving with the gate reopened by componentDidUpdate, it
      // would paint "something went wrong" over a healthy clip.
      const isBenignPlayRejection = isAbortError(args[0]) || isNotAllowedError(args[0]);

      if (!this.hasReportedReady && !isBenignPlayRejection) {
        this.setState({ hasError: true });
      }

      if (onError) {
        onError(...args);
        return;
      }

      onVideoPlayerError && onVideoPlayerError(args[0]);
    };

    private handleRetry = (): void => {
      this.hasReportedReady = false;
      this.setState({ hasError: false });
    };

    render(): ReactNode {
      const { hasError } = this.state;
      const { onVideoPlayerError, onError, onReady, ...otherProps } = this.props;

      if (hasError) {
        return (
          <SErrorWrapper>
            <SErrorTitle>
              <Translate str="frontend.media_player.video_player.errors.something_went_wrong" />
            </SErrorTitle>
            <SErrorRetryButton type="button" onClick={this.handleRetry}>
              <Translate str="frontend.media_player.audio_player.reload_message" />
            </SErrorRetryButton>
          </SErrorWrapper>
        );
      }

      return (
        <WrappedComponent
          {...otherProps}
          onReady={this.handleReady}
          onError={this.handleError}
        />
      );
    }
  };
